import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserRole, RBACPermissions } from '../types/nativeComms';

interface NativeCommsRBACContextType {
  userRole: UserRole;
  permissions: RBACPermissions;
  setUserRole: (role: UserRole) => void;
  hasPermission: (permission: keyof RBACPermissions) => boolean;
}

const NativeCommsRBACContext = createContext<NativeCommsRBACContextType | undefined>(undefined);

// Default permissions for each role
const rolePermissions: Record<UserRole, RBACPermissions> = {
  analyst: {
    canViewHealth: true,
    canTestSend: true,
    canRetryMessages: false,
    canEditTemplates: false,
    canReplayDLQ: false,
    canConfigureProviders: false,
    canPerformAdminActions: false,
    canManageNumbers: false,
    canUploadSuppressionLists: false,
    canRotateSecrets: false,
  },
  engineer: {
    canViewHealth: true,
    canTestSend: true,
    canRetryMessages: true,
    canEditTemplates: true,
    canReplayDLQ: true,
    canConfigureProviders: false,
    canPerformAdminActions: false,
    canManageNumbers: false,
    canUploadSuppressionLists: false,
    canRotateSecrets: false,
  },
  admin: {
    canViewHealth: true,
    canTestSend: true,
    canRetryMessages: true,
    canEditTemplates: true,
    canReplayDLQ: true,
    canConfigureProviders: true,
    canPerformAdminActions: true,
    canManageNumbers: true,
    canUploadSuppressionLists: true,
    canRotateSecrets: true,
  },
};

export const NativeCommsRBACProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userRole, setUserRole] = useState<UserRole>('analyst');

  // In production, this would be fetched from user session/auth
  useEffect(() => {
    const savedRole = localStorage.getItem('nativeCommsUserRole') as UserRole;
    if (savedRole && rolePermissions[savedRole]) {
      setUserRole(savedRole);
    }
  }, []);

  const permissions = rolePermissions[userRole];

  const hasPermission = (permission: keyof RBACPermissions): boolean => {
    return permissions[permission] || false;
  };

  const handleSetUserRole = (role: UserRole) => {
    setUserRole(role);
    localStorage.setItem('nativeCommsUserRole', role);
  };

  return (
    <NativeCommsRBACContext.Provider
      value={{
        userRole,
        permissions,
        setUserRole: handleSetUserRole,
        hasPermission,
      }}
    >
      {children}
    </NativeCommsRBACContext.Provider>
  );
};

export const useNativeCommsRBAC = (): NativeCommsRBACContextType => {
  const context = useContext(NativeCommsRBACContext);
  if (context === undefined) {
    throw new Error('useNativeCommsRBAC must be used within a NativeCommsRBACProvider');
  }
  return context;
};

// Permission-based component wrappers
export const RequirePermission: React.FC<{
  permission: keyof RBACPermissions;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ permission, children, fallback = null }) => {
  const { hasPermission } = useNativeCommsRBAC();
  
  if (!hasPermission(permission)) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
};

export const RequireRole: React.FC<{
  role: UserRole;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ role, children, fallback = null }) => {
  const { userRole } = useNativeCommsRBAC();
  
  if (userRole !== role) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
};